import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { decode } from 'html-entities';
import { shouldTranslateTo } from '../utils/utils';
import { Title, Subtitle, ContainerFlexColumn } from './Utils/CustomClasses';
import styled from 'styled-components';

const Partners = styled(ContainerFlexColumn)`
  background-color: ${({ color }) => (color ? color : '#fff')};
`;

const SectionTitle = styled(Title)`
  color: ${({ textcolor }) => (textcolor ? textcolor : '#154549')};
  margin-top: 50px;
`;

const PartnerBox = styled.div`
  background-color: #fff;
  border-radius: 26px;
  box-shadow: 0 5px 9px -2px #b5b5b5;
  border: 1px solid #eee;
  padding: 26px 26px 30px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const FlexRowPartners = styled.div`
  display: flex;
  flex-direction: row;
  gap: 30px;
  justify-content: center;
  margin-top: 30px;
  width: 100%;
  & > div {
    flex-basis: 45%;
  }
  @media (max-width: 979px) {
    flex-direction: column;
    margin-top: 20px;
    & > div {
      width: 100%;
    }
  }
`;

const PartnerLogoContainer = styled.div`
  width: 300px;
  height: 120px;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  margin-bottom: 20px;
  img {
    width: 100%;
    object-fit: contain;
  }
`;

const PartnerText = styled.p`
  font-size: 26px;
  line-height: 32px;
  margin: 20px 0;
  text-align: center;
`;

const PartnersPanel = ({ color, textcolor }) => {
  const [translations, setTranslations] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchTranslations = async () => {
      try {
        const language = shouldTranslateTo();
        const response = await axios.get(`/api/page-text/partners/${language}`);

        // Transform the response data into an object with textId as key
        const translationsMap = response.data.reduce((acc, item) => {
          acc[item.textId] = item.text;
          return acc;
        }, {});
        setTranslations(translationsMap);
        console.log('Translations loaded:', translationsMap); // Debug log
      } catch (error) {
        console.error('Error fetching translations:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTranslations();
  }, []);

  const investmentPartners = [
    {
      name: 'Nesta',
      logo: '/images/partners/11.jpeg',
      website: 'https://www.nesta.org.uk/project/mission-studio/'
    },
    {
      name: 'Founders Factory',
      logo: '/images/partners/2.jpeg',
      website: 'https://foundersfactory.com/'
    },
    {
      name: 'Enterprise Ireland',
      logo: '/images/partners/3.jpeg',
      website: 'https://www.enterprise-ireland.com/en/'
    },
    {
      name: 'Fundação Ageas',
      logo: '/images/partners/5.jpeg',
      website: 'https://www.grupoageas.pt/fundacao-ageas'
    },
    {
      name: 'UKRI Healthy Ageing Challenge',
      logo: '/images/partners/9.jpeg',
      website: 'https://www.ukri.org/councils/innovate-uk/'
    }
  ];

  const commercialPartners = [
    {
      name: 'Câmara Municipal do Porto',
      logo: '/images/partners/7.jpeg',
      website: 'https://www.cm-porto.pt/'
    },
    {
      name: 'Reformers',
      logo: '/images/partners/6.jpeg',
      website: 'http://reformers.pt'
    },
    {
      name: 'Rural Move',
      logo: '/images/partners/8.jpeg',
      website: 'https://plataforma.ruralmove.org/'
    },
    {
      name: 'NEST',
      logo: '/images/partners/10.jpeg',
      website: 'https://www.nestportugal.pt/'
    },
    {
      name: 'IRIS',
      logo: '/images/partners/4.jpeg',
      website: 'https://iris-social.org/'
    }
  ];

  const renderPartnerPanels = (partners) => {
    return partners.reduce((rows, partner, index) => {
      if (index % 2 === 0) {
        rows.push(
          <FlexRowPartners key={index}>
            <PartnerBox>
              <PartnerLogoContainer>
                <img src={partner.logo} alt={partner.name} />
              </PartnerLogoContainer>
              <a
                href={partner.website}
                target="_blank"
                rel="noopener noreferrer"
              >
                {translations[75] ? decode(translations[75]) : 'Visit Website'} {/* Visit Website text */}
              </a>
            </PartnerBox>
            {partners[index + 1] && (
              <PartnerBox>
                <PartnerLogoContainer>
                  <img src={partners[index + 1].logo} alt={partners[index + 1].name} />
                </PartnerLogoContainer>
                <a
                  href={partners[index + 1].website}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {decode(translations[75])} {/* Visit Website text */}
                </a>
              </PartnerBox>
            )}
          </FlexRowPartners>
        );
      }
      return rows;
    }, []);
  };

  if (isLoading) {
    return (
      <div className="card-body text-center">
        <div
          className="spinner-border homestays-spinner"
          style={{ marginTop: '100px' }}
          role="status"
        >
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <Partners color={color} center>
      <Title
        color={textcolor}
        size="30px"
        sizemd="1.6rem"
        sizesm="30px"
        center
        margin="0 10vw 24px 10vw"
        marginmd="0 10vw 24px 10vw"
        marginsm="20px 0 24px 0"
      >
        {translations[69] ? decode(translations[69]) : 'Our Partners'} {/* Our Partners */}
      </Title>

      <Subtitle
        center
        weight="300"
        color={textcolor}
        size="22px"
        sizemd="22px"
        sizesm="22px"
        margin="0 0 50px 0"
        marginsm="0 8px 40px 8px"
      >
        <PartnerText>{translations[70] ? decode(translations[70]) : ''}</PartnerText>
      </Subtitle>

      <SectionTitle
        color={textcolor}
        size="24px"
        sizemd="1.4rem"
        sizesm="24px"
        center
      >
        {translations[73] ? decode(translations[73]) : 'Investment Partners'} {/* Investment Partners */}
      </SectionTitle>
      {renderPartnerPanels(investmentPartners)}

      <SectionTitle
        color={textcolor}
        size="24px"
        sizemd="1.4rem"
        sizesm="24px"
        center
      >
        {translations[74] ? decode(translations[74]) : 'Commercial Partners'} {/* Commercial Partners */}
      </SectionTitle>
      {renderPartnerPanels(commercialPartners)}


    </Partners>
  );
};

export default PartnersPanel;
